/**
 * URL-driven filter/sort state for Explore.
 * All filter state lives in query params for shareability and back/forward.
 *
 * URL schema:
 *   mode=do|places|farms (default: places)
 *   when=upcoming|today|weekend (default: upcoming, only for "do")
 *   type=events,outings (default: all, only for "do")
 *   sort=soonest|nearest|az|relevance (default depends on mode)
 *   tags=tag1,tag2 (only for "places")
 */

export type WhenFilter = "upcoming" | "today" | "weekend";
export type ContentTypeFilter = "events" | "outings";
export type SortOption = "soonest" | "nearest" | "az" | "relevance";

export interface ExploreParams {
  when: WhenFilter;
  contentTypes: ContentTypeFilter[];
  sort: SortOption;
  tags: string[];
}

const DEFAULT_WHEN: WhenFilter = "upcoming";
const DEFAULT_CONTENT_TYPES: ContentTypeFilter[] = ["events", "outings"];

export function defaultSortForMode(mode: string): SortOption {
  return mode === "do" ? "soonest" : "relevance";
}

export function parseExploreParams(
  searchParams: URLSearchParams,
  mode: string
): ExploreParams {
  // when
  const whenRaw = searchParams.get("when");
  const when: WhenFilter =
    whenRaw === "today" || whenRaw === "weekend" ? whenRaw : DEFAULT_WHEN;

  // content types
  const typeRaw = searchParams.get("type");
  let contentTypes: ContentTypeFilter[] = DEFAULT_CONTENT_TYPES;
  if (typeRaw) {
    const parsed = typeRaw
      .split(",")
      .filter((t): t is ContentTypeFilter => t === "events" || t === "outings");
    if (parsed.length > 0) contentTypes = parsed;
  }

  // sort
  const sortRaw = searchParams.get("sort");
  const validSorts: SortOption[] = ["soonest", "nearest", "az", "relevance"];
  const sort: SortOption = validSorts.includes(sortRaw as SortOption)
    ? (sortRaw as SortOption)
    : defaultSortForMode(mode);

  // tags
  const tagsRaw = searchParams.get("tags");
  const tags = tagsRaw ? tagsRaw.split(",").filter(Boolean) : [];

  return { when, contentTypes, sort, tags };
}

/** Build a partial URLSearchParams update, removing defaults to keep URLs clean */
export function buildExploreParamUpdates(
  params: Partial<ExploreParams>,
  mode: string
): Record<string, string | null> {
  const updates: Record<string, string | null> = {};

  if (params.when !== undefined) {
    updates.when = params.when === DEFAULT_WHEN ? null : params.when;
  }

  if (params.contentTypes !== undefined) {
    const isDefault =
      params.contentTypes.length === DEFAULT_CONTENT_TYPES.length &&
      DEFAULT_CONTENT_TYPES.every((t) => params.contentTypes!.includes(t));
    updates.type = isDefault ? null : params.contentTypes.join(",");
  }

  if (params.sort !== undefined) {
    updates.sort =
      params.sort === defaultSortForMode(mode) ? null : params.sort;
  }

  if (params.tags !== undefined) {
    updates.tags = params.tags.length === 0 ? null : params.tags.join(",");
  }

  return updates;
}

/** Human-readable labels for filter chips */
export const WHEN_LABELS: Record<WhenFilter, string> = {
  upcoming: "Kommande",
  today: "Idag",
  weekend: "Denna helg",
};

export const SORT_LABELS: Record<SortOption, string> = {
  soonest: "Närmast i tid",
  nearest: "Närmast mig",
  az: "A–Ö",
  relevance: "Relevans",
};

export const CONTENT_TYPE_LABELS: Record<ContentTypeFilter, string> = {
  events: "Event & kurser",
  outings: "Utflyktstips",
};
